import './settings-leaf.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CompanyAddOn } from '@shared/add-ons.ts';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@shared/constants/languages.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { addOnsApi } from '../../api/add-ons.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { getDateLocale } from '../../i18n/locale.ts';
import { useLocalizedContent } from '../../i18n/localized.ts';
import { Alert, Button, Checkbox, LoadingBlock, TextArea, TextInput } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import LocalizedFields, {
  emptyTranslationDraft,
  translationDraftFrom,
  translationPayload,
  type TranslationDraft,
} from '../localized-fields.tsx';

interface Draft {
  id: string | null;
  name: string;
  nameTranslations: TranslationDraft;
  amountCents: number | null;
  allLocations: boolean;
  withTask: boolean;
  taskName: string;
  taskTranslations: TranslationDraft;
  taskDescription: string;
}

const blank = (): Draft => ({
  id: null,
  name: '',
  nameTranslations: emptyTranslationDraft(),
  amountCents: null,
  allLocations: false,
  withTask: false,
  taskName: '',
  taskTranslations: emptyTranslationDraft(),
  taskDescription: '',
});

const draftFrom = (addOn: CompanyAddOn): Draft => ({
  id: addOn.id,
  name: addOn.name,
  nameTranslations: translationDraftFrom(addOn.nameTranslations),
  amountCents: addOn.defaultAmountCents,
  allLocations: addOn.locationId === null,
  withTask: addOn.checklistTask !== null,
  taskName: addOn.checklistTask?.name ?? '',
  taskTranslations: translationDraftFrom(addOn.checklistTask?.nameTranslations),
  taskDescription: addOn.checklistTask?.description ?? '',
});

/**
 * Saved add-ons (migration 0093): the extras the office adds to one visit
 * with one tap from the visit's price card. Each has a name, a default
 * final price and, optionally, the checklist task it brings to that visit.
 * Archived rather than deleted, so visits that sold one keep their line.
 */
export default function AddOnsCard() {
  const { can } = useSession();
  // Add-ons are sold on scheduled visits; without scheduling there is nowhere to use one.
  if (!usePlanFeature('scheduling') || !can('templates:read')) return null;
  return <AddOnsCatalog />;
}

function AddOnsCatalog() {
  const { t } = useTranslation();
  const { company, currentLocation, can, currencyCode: locationCurrency } = useSession();
  const localized = useLocalizedContent();
  const queryClient = useQueryClient();
  const canWrite = can('templates:write');
  const canCompanyWide = can('company:write');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const key = ['settings-add-ons', company?.id, currentLocation?.id];
  const query = useQuery({ queryKey: key, queryFn: ({ signal }) => addOnsApi.list(true, signal) });
  const baseLanguage: SupportedLanguage = (SUPPORTED_LANGUAGES as readonly string[]).includes(company?.language ?? '')
    ? (company!.language as SupportedLanguage)
    : 'en';
  const currency = company?.currencyCode ?? 'USD';
  // A company-wide add-on is priced in the company's money; one this location
  // sells alone in the location's, which may be its own — as the Worker does.
  const sellerCurrency = (companyWide: boolean) => (companyWide ? currency : (locationCurrency ?? currency));
  const failed = (caught: unknown) =>
    setError(caught instanceof ApiRequestError ? caught.message : t('addOns.saveFailed'));
  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: ['settings-add-ons'] });
    await queryClient.invalidateQueries({ queryKey: ['visit-add-ons'] });
  };
  const save = useMutation({
    mutationFn: async (value: Draft) => {
      const name = value.name.trim();
      const checklistTask = value.withTask
        ? {
            name: value.taskName.trim(),
            nameTranslations: translationPayload(value.taskTranslations, baseLanguage, value.taskName.trim()),
            description: value.taskDescription.trim() || null,
          }
        : null;
      const body = {
        name,
        nameTranslations: translationPayload(value.nameTranslations, baseLanguage, name),
        defaultAmountCents: value.amountCents!,
        checklistTask,
      };
      return value.id
        ? addOnsApi.update(value.id, body)
        : addOnsApi.create({ ...body, scope: value.allLocations ? 'company' : 'location' });
    },
    onSuccess: async () => {
      setDraft(null);
      await refresh();
    },
    onError: failed,
  });
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
    onError: failed,
  });
  const items = query.data?.items ?? [];
  const active = items.filter((item) => item.archivedAt === null);
  const archived = items.filter((item) => item.archivedAt !== null);
  const editable = (item: CompanyAddOn) => canWrite && (item.locationId !== null || canCompanyWide);
  // A manager orders their location's own add-ons; moving an every-location
  // one is the owner's, as the server enforces.
  const movable = active.filter(editable);
  const move = (item: CompanyAddOn, step: -1 | 1) => {
    const ids = movable.map((entry) => entry.id);
    const index = ids.indexOf(item.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + step, 0, moved!);
    act.mutate(() => addOnsApi.reorder(ids));
  };
  const valid =
    draft !== null &&
    draft.name.trim().length > 0 &&
    draft.amountCents !== null &&
    draft.amountCents !== 0 &&
    (!draft.withTask || draft.taskName.trim().length > 0);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft && valid && !save.isPending) save.mutate(draft);
  };
  const busy = save.isPending || act.isPending;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="add-ons-heading">
      <div className="work-section-heading">
        <h2 id="add-ons-heading" className="mb-2 text-lg font-semibold">
          {t('addOns.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('addOns.hint')}</p>
      {query.isPending ? <LoadingBlock /> : null}
      {query.isError ? <Alert tone="error">{t('addOns.loadFailed')}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}
      {query.isSuccess && active.length === 0 ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('addOns.empty')}</p>
      ) : null}
      {active.length > 0 ? (
        <ul className="mb-4 divide-y divide-[color:var(--color-line)] text-sm" aria-label={t('addOns.heading')}>
          {active.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                <span className="font-medium">{localized.text(item.name, item.nameTranslations)}</span>{' '}
                <span className="tabular">
                  {formatCents(item.defaultAmountCents, item.currencyCode, getDateLocale())}
                </span>
                {item.currencyCode !== sellerCurrency(item.locationId === null) ? (
                  <span className="ml-2 badge">{t('addOns.otherCurrency', { currency: item.currencyCode })}</span>
                ) : null}
                {item.locationId === null ? <span className="ml-2 badge">{t('addOns.allLocations')}</span> : null}
                {item.checklistTask ? (
                  <span className="block text-xs text-[color:var(--color-muted)]">
                    {t('addOns.taskSummary', {
                      task: localized.text(item.checklistTask.name, item.checklistTask.nameTranslations),
                    })}
                  </span>
                ) : null}
              </span>
              {editable(item) ? (
                <span className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    disabled={busy || movable[0]?.id === item.id}
                    aria-label={t('addOns.moveUp', { name: item.name })}
                    onClick={() => move(item, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy || movable.at(-1)?.id === item.id}
                    aria-label={t('addOns.moveDown', { name: item.name })}
                    onClick={() => move(item, 1)}
                  >
                    ↓
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={() => setDraft(draftFrom(item))}>
                    {t('addOns.edit')}
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={() => act.mutate(() => addOnsApi.archive(item.id))}>
                    {t('addOns.archive')}
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {archived.length > 0 ? (
        <div className="mb-4">
          <Button variant="ghost" onClick={() => setShowArchived((value) => !value)}>
            {showArchived ? t('addOns.hideArchived') : t('addOns.showArchived', { count: archived.length })}
          </Button>
          {showArchived ? (
            <ul className="mt-2 divide-y divide-[color:var(--color-line)] text-sm">
              {archived.map((item) => (
                <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-[color:var(--color-muted)]">
                    {localized.text(item.name, item.nameTranslations)}
                  </span>
                  {editable(item) ? (
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => act.mutate(() => addOnsApi.restore(item.id))}
                    >
                      {t('addOns.restore')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {canWrite && !draft ? (
        <Button variant="secondary" disabled={busy || !query.isSuccess} onClick={() => setDraft(blank())}>
          {t('addOns.add')}
        </Button>
      ) : null}
      {draft ? (
        <form onSubmit={submit} className="rounded-md border border-[color:var(--color-line)] p-3">
          <fieldset disabled={save.isPending} className="contents">
            <TextInput
              label={t('addOns.name')}
              value={draft.name}
              maxLength={160}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
            <MoneyInput
              label={t('addOns.amount')}
              hint={t('addOns.amountHint')}
              valueCents={draft.amountCents}
              currencyCode={sellerCurrency(draft.allLocations)}
              onChangeCents={(amountCents) => setDraft({ ...draft, amountCents })}
            />
            {draft.id === null && canCompanyWide ? (
              <Checkbox
                label={t('addOns.allLocationsOption')}
                checked={draft.allLocations}
                onChange={(event) => setDraft({ ...draft, allLocations: event.target.checked })}
              />
            ) : null}
            <Checkbox
              label={t('addOns.withTask')}
              hint={t('addOns.withTaskHint')}
              checked={draft.withTask}
              onChange={(event) => setDraft({ ...draft, withTask: event.target.checked })}
            />
            {draft.withTask ? (
              <>
                <TextInput
                  label={t('addOns.taskName')}
                  value={draft.taskName}
                  maxLength={200}
                  onChange={(event) => setDraft({ ...draft, taskName: event.target.value })}
                />
                <TextArea
                  label={t('addOns.taskDescription')}
                  value={draft.taskDescription}
                  maxLength={2000}
                  rows={2}
                  onChange={(event) => setDraft({ ...draft, taskDescription: event.target.value })}
                />
              </>
            ) : null}
            <LocalizedFields
              baseLanguage={baseLanguage}
              fields={[
                {
                  key: 'name',
                  label: t('addOns.name'),
                  valueFor: (language) => draft.nameTranslations[language],
                  onChange: (language, value) =>
                    setDraft({ ...draft, nameTranslations: { ...draft.nameTranslations, [language]: value } }),
                },
                ...(draft.withTask
                  ? [
                      {
                        key: 'task',
                        label: t('addOns.taskName'),
                        valueFor: (language: SupportedLanguage) => draft.taskTranslations[language],
                        onChange: (language: SupportedLanguage, value: string) =>
                          setDraft({ ...draft, taskTranslations: { ...draft.taskTranslations, [language]: value } }),
                      },
                    ]
                  : []),
              ]}
            />
          </fieldset>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="submit" variant="primary" busy={save.isPending} disabled={!valid}>
              {t('addOns.save')}
            </Button>
            <Button disabled={save.isPending} onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
